<template>
  <MainLayoutComponent
    :user-info="mainLayoutUiModel.userInfo"
    :is-user-info-loading="isUserInfoLoading"
    :are-side-nav-sections-loading="areSideNavSectionsLoading"
    :is-content-loading="isContentLoading"
    @logout="emit('logout')"
    @update:user-info-select-value="emit('update:selectedClientId', $event)"
  >
    <template #user-info-actions>
      <button
        type="button"
        class="flex w-full items-center gap-1 rounded-sm p-3 text-left text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-grey"
      >
        <Icon :icon="IconName.Team" class="h-6 w-6 shrink-0" aria-hidden="true" />
        <span class="typo-text2 truncate tracking-[-0.02em]">
          {{ t('common.side-nav.manage-dashboard-access') }}
        </span>
      </button>
    </template>

    <template #side-nav-sections>
      <SideNavSection
        v-if="canDisplayCustomisationAppSection"
        :active-item-id="mainLayoutUiModel.activeItemNavigationElementId"
        divider-after
      >
        <LineButton
          v-capture-button-name="'open-customisation-app'"
          :disabled="!mainLayoutUiModel.userInfo.selectValue"
          :content="t('customisation-app.side-nav.button')"
          :variant="LineButtonTypes.Primary"
          :color="LineButtonColor.Primary"
          :right-icon="{ icon: IconName.Edit }"
          @click="emit('openCustomisationApp')"
        />
      </SideNavSection>

      <SideNavSection
        v-if="getSection(COCKPIT_NAVIGATION_SECTION_IDS.HOME)"
        :items="getSection(COCKPIT_NAVIGATION_SECTION_IDS.HOME)?.items"
        :active-item-id="mainLayoutUiModel.activeItemNavigationElementId"
        divider-after
        @item-click="emit('navigate', $event)"
      />

      <AccessCodeCard
        v-if="mainLayoutUiModel.clientInfos.subscriptionCode"
        capture-button-name="copy-subscription-access-code"
        :code="mainLayoutUiModel.clientInfos.subscriptionCode"
        :copied-feedback="t('common.code-copied')"
        :label="t('main.access-code')"
        @copied="emit('accessCodeCopied')"
      />

      <SideNavSection
        v-if="getSection(COCKPIT_NAVIGATION_SECTION_IDS.SUBSCRIPTION)"
        :title="getSection(COCKPIT_NAVIGATION_SECTION_IDS.SUBSCRIPTION)?.title"
        :subtitle="getSection(COCKPIT_NAVIGATION_SECTION_IDS.SUBSCRIPTION)?.subtitle"
        :active-item-id="mainLayoutUiModel.activeItemNavigationElementId"
      >
        <SideNavDivider v-if="mainLayoutUiModel.subscriptionSubscriberProgress" />

        <div v-if="mainLayoutUiModel.subscriptionSubscriberProgress">
          <div v-if="mainLayoutUiModel.subscriptionSubscriberProgress.isLoading" class="flex flex-col gap-2">
            <div class="h-3 w-24 animate-pulse rounded-sm bg-app-grey-light" />
            <div class="h-2 w-full animate-pulse rounded-full bg-app-grey-light" />
          </div>
          <Quota
            v-else
            class="w-full"
            :label="t('main.subscription-subscriber-progress.title')"
            :start="`${mainLayoutUiModel.subscriptionSubscriberProgress.current} / ${mainLayoutUiModel.subscriptionSubscriberProgress.total}`"
            :end="`${mainLayoutUiModel.subscriptionSubscriberProgress.percentage}%`"
            :progress="mainLayoutUiModel.subscriptionSubscriberProgress.percentage"
          />
        </div>
      </SideNavSection>

      <SideNavSection
        v-if="getSection(COCKPIT_NAVIGATION_SECTION_IDS.SUBSCRIPTION)"
        :items="getSection(COCKPIT_NAVIGATION_SECTION_IDS.SUBSCRIPTION)?.items"
        :active-item-id="mainLayoutUiModel.activeItemNavigationElementId"
        divider-after
        @item-click="emit('navigate', $event)"
      />

      <SideNavSection
        :title="getSection(COCKPIT_NAVIGATION_SECTION_IDS.GAMES)?.title"
        :subtitle="getSection(COCKPIT_NAVIGATION_SECTION_IDS.GAMES)?.subtitle"
        :items="getSection(COCKPIT_NAVIGATION_SECTION_IDS.GAMES)?.items"
        :active-item-id="mainLayoutUiModel.activeItemNavigationElementId"
        divider-after
        @item-click="emit('navigate', $event)"
      >
        <LineButton
          v-if="mainLayoutUiModel.isCreateNewGameButtonShown"
          v-capture-button-name="'open-create-new-game'"
          class="[&>div+button]:px-6"
          :content="t('game-creation.button')"
          :variant="LineButtonTypes.Primary"
          :color="LineButtonColor.Primary"
          @click="emit('openCreateGame')"
        />
      </SideNavSection>

      <SideNavSection
        v-if="getSection(COCKPIT_NAVIGATION_SECTION_IDS.COMMUNICATION)"
        :title="getSection(COCKPIT_NAVIGATION_SECTION_IDS.COMMUNICATION)?.title"
        :subtitle="getSection(COCKPIT_NAVIGATION_SECTION_IDS.COMMUNICATION)?.subtitle"
        :items="getSection(COCKPIT_NAVIGATION_SECTION_IDS.COMMUNICATION)?.items"
        :active-item-id="mainLayoutUiModel.activeItemNavigationElementId"
        @item-click="emit('navigate', $event)"
      />
    </template>

    <template #side-nav-footer>
      <slot name="side-nav-footer">
        <SideNavItem :label="t('help-center.side-nav-label')" :icon="IconName.QuestionCircle" :active="false" />
      </slot>
    </template>

    <slot />

    <template #overlay>
      <slot name="overlay" />
    </template>
  </MainLayoutComponent>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { IconName } from '@kiplin/web-common/icon';
import { Icon, LineButton, LineButtonColor, LineButtonTypes, Quota } from '@kiplin/web-common/design-system';
import type {
  SideNavItem as SideNavItemType,
  SideNavSection as SideNavSectionType,
} from '@design-system/side-nav/application/component/SideNav/SideNav.types';
import SideNavDivider from '@design-system/side-nav/application/component/SideNavDivider/SideNavDivider.vue';
import SideNavItem from '@design-system/side-nav/application/component/SideNavItem/SideNavItem.vue';
import SideNavSection from '@design-system/side-nav/application/component/SideNavSection/SideNavSection.vue';
import AccessCodeCard from '@client/entity/client/application/component/AccessCodeCard/AccessCodeCard.vue';
import { COCKPIT_NAVIGATION_SECTION_IDS } from '@client/composition/main-layout/domain/constant/cockpit-navigation-section-ids.constant';
import MainLayoutComponent from '@shared/common/main-layout/application/component/MainLayoutComponent.vue';
import type { ClientMainLayoutComponentProps } from './ClientMainLayoutComponent.types';

const props = defineProps<ClientMainLayoutComponentProps>();
const emit = defineEmits<{
  logout: [];
  'update:selectedClientId': [clientId: string];
  navigate: [item: SideNavItemType];
  openCustomisationApp: [];
  openCreateGame: [];
  accessCodeCopied: [];
}>();
const { t } = useI18n();

const getSection = (sectionId: string): SideNavSectionType | null =>
  props.mainLayoutUiModel.navigationElements.find((candidate) => candidate.id === sectionId) ?? null;
</script>
